import { Minus, Pencil, Plus, TriangleAlert } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import type { CategoryDto, MenuSummaryDto, PublishResultDto } from "../../../shared/api-types";
import type { DiffItem } from "../../../shared/snapshot";
import { ApiRequestError } from "../../api/client";
import { menuApi, usePublishPreview } from "../../api/queries";
import { Modal } from "../../components/kit";
import { Alert, ICON } from "../../components/ui";
import { formatWhen, plural } from "../../lib/format";

const errorText = (e: unknown) =>
  e instanceof Error ? e.message : "A apărut o eroare neașteptată. Încearcă din nou.";

function Field({
  id,
  label,
  value,
  onChange,
  error,
  placeholder,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field-input"
        value={value}
        maxLength={80}
        placeholder={placeholder}
        aria-invalid={error ? "true" : "false"}
        aria-describedby={error ? `${id}-e` : undefined}
        data-autofocus={autoFocus ? "" : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <span id={`${id}-e`} className="field-error">
          {error}
        </span>
      )}
    </div>
  );
}

/** "Meniu nou" / "Redenumește meniul". */
export function MenuNameDialog({
  mode,
  initial = "",
  onClose,
  onSubmit,
}: {
  mode: "create" | "rename";
  initial?: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [value, setValue] = useState(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!value.trim()) return setError("Adaugă un nume pentru meniu.");
    setBusy(true);
    try {
      await onSubmit(value.trim());
    } catch (err) {
      setError(err instanceof ApiRequestError ? (err.fields.name ?? err.message) : errorText(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title={mode === "create" ? "Meniu nou" : "Redenumește meniul"}
      body={
        mode === "create"
          ? "Meniul nou pornește ca ciornă. Clienții nu îl văd până îl publici."
          : "Numele apare în meniul public dacă ai mai multe meniuri active."
      }
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Renunță
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => submit()}>
            {mode === "create" ? "Creează meniul" : "Salvează"}
          </button>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <Field
          id="dlg-menu"
          label="Nume meniu"
          value={value}
          onChange={(v) => {
            setValue(v);
            setError("");
          }}
          error={error}
          placeholder="ex. Meniu de prânz"
          autoFocus
        />
      </form>
    </Modal>
  );
}

/** Generic confirmation (archive, unpublish, delete, duplicate, remove product). */
export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  danger = true,
  onClose,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  return (
    <Modal
      title={title}
      body={body}
      alert={danger}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            data-autofocus={danger ? "" : undefined}
          >
            Renunță
          </button>
          <button
            type="button"
            className={`btn ${danger ? "btn-danger" : "btn-primary"}`}
            disabled={busy}
            data-autofocus={danger ? undefined : ""}
            onClick={async () => {
              setBusy(true);
              setError("");
              try {
                await onConfirm();
              } catch (err) {
                setError(errorText(err));
                setBusy(false);
              }
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
    </Modal>
  );
}

/** "Categorie nouă" / "Redenumește categoria" with the optional English name. */
export function CategoryDialog({
  category,
  onClose,
  onSubmit,
}: {
  category?: CategoryDto;
  onClose: () => void;
  onSubmit: (input: { name: string; translation: { lang: "en"; name: string } }) => Promise<void>;
}) {
  const [ro, setRo] = useState(category?.name ?? "");
  const [en, setEn] = useState(category?.translations.en?.name ?? "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!ro.trim()) return setError("Adaugă numele categoriei.");
    setBusy(true);
    try {
      await onSubmit({ name: ro.trim(), translation: { lang: "en", name: en.trim() } });
    } catch (err) {
      setError(err instanceof ApiRequestError ? (err.fields.name ?? err.message) : errorText(err));
      setBusy(false);
    }
  }
  return (
    <Modal
      title={category ? "Redenumește categoria" : "Categorie nouă"}
      body="Modificarea intră în ciornă."
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Renunță
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => submit()}>
            {category ? "Salvează" : "Adaugă categoria"}
          </button>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="stack">
        <Field
          id="dlg-cro"
          label="Nume · română (obligatoriu)"
          value={ro}
          onChange={(v) => {
            setRo(v);
            setError("");
          }}
          error={error}
          placeholder="ex. Aperitive"
          autoFocus
        />
        <Field
          id="dlg-cen"
          label="Nume · English (opțional)"
          value={en}
          onChange={setEn}
          placeholder="ex. Starters"
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

/** Deleting a category: move its products elsewhere or remove them too (prototype `catDelete`). */
export function CategoryDeleteDialog({
  category,
  productCount,
  others,
  onClose,
  onConfirm,
}: {
  category: CategoryDto;
  productCount: number;
  others: CategoryDto[];
  onClose: () => void;
  onConfirm: (input: { mode: "move"; targetCategoryId: string } | { mode: "delete" }) => Promise<void>;
}) {
  const [choice, setChoice] = useState<"move" | "delete">(productCount && others.length ? "move" : "delete");
  const [target, setTarget] = useState(others[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const n = productCount;
  return (
    <Modal
      alert
      title={`Ștergi categoria „${category.name}”?`}
      body={
        n
          ? `Categoria are ${plural(n, "produs", "produse")}. Alege ce se întâmplă cu ${n === 1 ? "el" : "ele"}. Modificarea intră în ciornă.`
          : "Categoria nu are produse. Modificarea intră în ciornă."
      }
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} data-autofocus="">
            Renunță
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(
                  n && choice === "move" ? { mode: "move", targetCategoryId: target } : { mode: "delete" },
                );
              } catch (err) {
                setError(errorText(err));
                setBusy(false);
              }
            }}
          >
            {n && choice === "delete" ? "Șterge categoria și produsele" : "Șterge categoria"}
          </button>
        </>
      }
    >
      {n > 0 && (
        <fieldset
          style={{ border: 0, margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}
        >
          <legend className="field-label" style={{ padding: 0, marginBottom: 6 }}>
            Produsele din categorie
          </legend>
          {others.length > 0 && (
            <label className="radio">
              <input
                type="radio"
                name="cat-del"
                checked={choice === "move"}
                onChange={() => setChoice("move")}
              />
              Mută produsele în altă categorie
            </label>
          )}
          {choice === "move" && others.length > 0 && (
            <div style={{ paddingLeft: 30 }}>
              <select
                className="field-input"
                aria-label="Categoria în care muți produsele"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                {others.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <label className="radio">
            <input
              type="radio"
              name="cat-del"
              checked={choice === "delete" || !others.length}
              onChange={() => setChoice("delete")}
            />
            Elimină și {plural(n, "produsul", "produsele")}
          </label>
        </fieldset>
      )}
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
    </Modal>
  );
}

const KIND = {
  add: { icon: Plus, color: "var(--color-success)" },
  chg: { icon: Pencil, color: "var(--color-warning)" },
  rem: { icon: Minus, color: "var(--color-error)" },
} as const;

export function DiffList({ items }: { items: DiffItem[] }) {
  if (!items.length) return null;
  return (
    <ul className="diff-list">
      {items.map((d) => {
        const k = KIND[d.kind];
        return (
          <li key={`${d.kind}|${d.productId ?? d.categoryId ?? ""}|${d.subject}|${d.change}`}>
            <span style={{ color: k.color, display: "flex", paddingTop: 2 }}>
              <k.icon size={14} strokeWidth={1.75} aria-hidden="true" />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span className="sub">{d.subject}</span>
              <span className="chg">{d.change}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Publish dialog (prototype `publish`): diff, blockers with "Deschide produsul", legal note, retry.
 * The idempotency key lives as long as the previewed revision, so a retry after a lost response cannot
 * publish twice.
 */
export function PublishDialog({
  rid,
  menu,
  publicUrl,
  onClose,
  onOpenProduct,
  onPublished,
}: {
  rid: string;
  menu: MenuSummaryDto;
  publicUrl: string;
  onClose: () => void;
  onOpenProduct: (productId: string) => void;
  onPublished: (r: PublishResultDto) => void;
}) {
  const preview = usePublishPreview(rid, menu.id);
  const key = useRef<{ revision: number; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  // Every statement the restaurant must confirm (allergens, frozen products) has its own tick.
  const [acks, setAcks] = useState<ReadonlySet<string>>(new Set());
  const p = preview.data;
  const hard = p?.blockers.filter((b) => !b.acknowledgeable) ?? [];
  const notices = p?.blockers.filter((b) => b.acknowledgeable) ?? [];
  const noticeOk = notices.every((b) => acks.has(b.code));
  const first = p?.firstPublish ?? !menu.publishedVersionId;
  const n = p?.changes.length ?? 0;
  const title = first
    ? `Publici „${menu.name}” pentru prima dată?`
    : n === 0
      ? `Publici din nou „${menu.name}”?`
      : `Publici ${n === 1 ? "o modificare" : `${n} modificări`}?`;

  async function publish() {
    if (!p) return;
    if (!key.current || key.current.revision !== p.draftRevision) {
      key.current = { revision: p.draftRevision, key: crypto.randomUUID() };
    }
    setBusy(true);
    setFailure("");
    try {
      onPublished(
        await menuApi(rid).publish(menu.id, p.draftRevision, key.current.key, notices.length > 0 && noticeOk),
      );
    } catch (err) {
      setBusy(false);
      if (
        err instanceof ApiRequestError &&
        (err.code === "draft_changed" || err.code === "publish_blocked")
      ) {
        await preview.refetch();
        setFailure(err.message);
        return;
      }
      setFailure(
        `Publicarea a eșuat. Meniul public nu s-a schimbat și ciorna este păstrată. Poți reîncerca. (${errorText(err)})`,
      );
    }
  }

  const blocked = !p || hard.length > 0 || !noticeOk;
  return (
    <Modal
      title={title}
      body={`Clienții vor vedea noua versiune imediat. Linkul ${publicUrl.replace(/^https?:\/\//, "")} și codul QR rămân aceleași.`}
      onClose={onClose}
      actions={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            data-autofocus={blocked ? "" : undefined}
          >
            Renunță
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={blocked || busy || !p?.canPublish}
            data-autofocus={blocked ? undefined : ""}
            onClick={publish}
          >
            {busy ? "Se publică…" : failure ? "Reîncearcă publicarea" : "Publică"}
          </button>
        </>
      }
    >
      {preview.isLoading && (
        <p className="note" role="status">
          Se pregătește lista modificărilor…
        </p>
      )}
      {preview.isError && (
        <Alert tone="error" live>
          {errorText(preview.error)}
        </Alert>
      )}
      {failure && (
        <Alert tone="error" live>
          {failure}
        </Alert>
      )}
      {p && <DiffList items={p.changes} />}
      {hard.length === 0 &&
        notices.map((b) => (
          <label key={b.code} className="check-row">
            <input
              type="checkbox"
              checked={acks.has(b.code)}
              onChange={(e) =>
                setAcks((s) => {
                  const next = new Set(s);
                  if (e.target.checked) next.add(b.code);
                  else next.delete(b.code);
                  return next;
                })
              }
            />
            <span>{b.text}</span>
          </label>
        ))}
      {p && hard.length > 0 && (
        <div className="gate" role="alert">
          <span className="row" style={{ gap: 8, fontWeight: 600, fontSize: 14 }}>
            <TriangleAlert {...ICON} color="var(--color-warning)" aria-hidden="true" />
            Rezolvă înainte de publicare
          </span>
          {hard.map((g) => (
            <div className="gate-row" key={`${g.code}-${g.productId ?? ""}`}>
              <span style={{ flex: "1 1 200px" }}>{g.text}</span>
              {g.productId && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => onOpenProduct(g.productId as string)}
                >
                  Deschide produsul
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="note">
        Informațiile despre alergeni sunt responsabilitatea restaurantului. Cerințele legale (Regulamentul UE
        1169/2011 și legislația națională) trebuie validate de un consultant juridic înainte de lansare.
      </p>
    </Modal>
  );
}

/** "Renunță la modificări": the draft returns to the published version. No undo yet (DEC-027). */
export function DiscardDialog({
  rid,
  menu,
  onClose,
  onDiscarded,
}: {
  rid: string;
  menu: MenuSummaryDto;
  onClose: () => void;
  onDiscarded: () => void;
}) {
  const preview = usePublishPreview(rid, menu.id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const n = preview.data?.changes.length ?? menu.pendingChanges ?? 0;
  return (
    <Modal
      alert
      title={`Renunți la ${n === 1 ? "o modificare nepublicată" : `${n} modificări nepublicate`}?`}
      body={`Ciorna revine la versiunea publicată ${formatWhen(menu.publishedAt)}. Disponibilitatea produselor nu se schimbă.`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} data-autofocus="">
            Păstrează ciorna
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={busy || !preview.data}
            onClick={async () => {
              if (!preview.data) return;
              setBusy(true);
              try {
                await menuApi(rid).discard(menu.id, preview.data.draftRevision);
                onDiscarded();
              } catch (err) {
                setError(errorText(err));
                setBusy(false);
                await preview.refetch();
              }
            }}
          >
            Renunță la modificări
          </button>
        </>
      }
    >
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
      {preview.data && <DiffList items={preview.data.changes} />}
    </Modal>
  );
}
